import styled from '@emotion/styled';
import Loader from '@view/components/Loader';
import { ComponentType, ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryErrorResetBoundary } from 'react-query';
import { Route, Routes } from 'react-router-dom';
import ContentLayout from './ContentLayout';
import ErrorFallback from './ErrorFallback';
import Header from './Header';
import Landing from '@view/components/Landing';
import DataObserver from '@view/components/DataObserver';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 1232px;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

type MainLayoutProps = { children: ReactNode };

const MainLayout = ({ children }: MainLayoutProps) => {
  const { property } = useStore();

  return (
    <>
      <DataObserver />
      {property.model ? (
        <Wrapper>
          <Header />
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary FallbackComponent={ErrorFallback} onReset={reset}>
                <Suspense fallback={<Loader fixed />}>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                  </Routes>
                  <PageContent>
                    <ContentLayout>{children}</ContentLayout>
                  </PageContent>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </Wrapper>
      ) : (
        <Loader fixed />
      )}
    </>
  );
};

const ObservedMainLayourt = observer(MainLayout);

/* eslint-disable react/display-name */
export const withMainLayout =
  <P extends object>(Component: ComponentType<P>) =>
  (Props: P) =>
    (
      <ObservedMainLayourt>
        <Component {...Props} />
      </ObservedMainLayourt>
    );

export default ObservedMainLayourt;
